import { useEffect, useState } from "react";
import * as yup from "yup";
import { cpf, cnpj } from "cpf-cnpj-validator";
import { fetchStateCities, fetchStatesUF } from "../../../../services/ibge";
import { useOnboarding } from "../../../../hooks/useOnboarding";
import { DropdownV2, FormCardV2, FormGridV2, InputV2, OptionV2 } from "../../../../components/FormControlsV2";
import { OnboardingFooterV2 } from "../../../../components/OnboardingV2/OnboardingFooterV2";
import { hasErrors, validateV2, ValidationErrorsV2 } from "../adapters/validation";
import { persistPayload } from "../adapters/payload";

type AssortmentFormV2 = {
  whoiam: OptionV2 | null;
  name: string;
  document: string;
  state_registration: string;
  phone: string;
  address: {
    state: OptionV2 | null;
    city: OptionV2 | null;
    street: string;
  };
};

const whoIamOptions = [
  { label: "Agropecuária", value: "agropecuaria" },
  { label: "Fazenda", value: "fazenda" },
];

const baseAssortment: AssortmentFormV2 = {
  whoiam: null,
  name: "",
  document: "",
  state_registration: "",
  phone: "",
  address: {
    state: null,
    city: null,
    street: "",
  },
};

const assortmentSchema = yup.object().shape({
  whoiam: yup.object().nullable().required("Selecione Agropecuária/Fazenda"),
  name: yup.string().matches(/^[^\s]+(\s+[^\s]+)*$/, "NOME FANTASIA é necessário").required("Campo obrigatório não informado."),
  document: yup
    .string()
    .notRequired()
    .test("documentCPF", "CPF informado inválido", (value) => !value || value.length > 11 || cpf.isValid(value))
    .test("documentCNPJ", "CNPJ informado inválido", (value) => !value || value.length <= 11 || cnpj.isValid(value)),
  address: yup.object({
    city: yup.object().nullable().required("Campo obrigatório não informado."),
    state: yup.object().nullable().required("Campo obrigatório não informado."),
    street: yup.string(),
  }),
});

export function CreateAssortmentV2() {
  const { payload, setPayload, setBlockNextStep, apiFieldErrors, setApiFieldErrors, clearStepError } = useOnboarding();
  const [value, setValue] = useState<AssortmentFormV2>(baseAssortment);
  const [states, setStates] = useState<OptionV2[]>([]);
  const [cities, setCities] = useState<OptionV2[]>([]);
  const [errors, setErrors] = useState<ValidationErrorsV2>({});

  async function validateAssortment(valueToValidate = value) {
    if (valueToValidate.whoiam?.value === "fazenda") {
      setErrors({});
      setBlockNextStep(false);
      clearStepError(1, 2);
      setApiFieldErrors((current) =>
        Object.fromEntries(Object.entries(current).filter(([field]) => !field.startsWith("assortment.")))
      );
      return true;
    }

    const validation = await validateV2(assortmentSchema, valueToValidate);
    setErrors(validation);
    const invalid = hasErrors(validation);
    setBlockNextStep(invalid);
    if (!invalid) {
      clearStepError(1, 2);
      setApiFieldErrors((current) =>
        Object.fromEntries(Object.entries(current).filter(([field]) => !field.startsWith("assortment.")))
      );
    }
    return !invalid;
  }

  function updateValue(nextValue: AssortmentFormV2) {
    setValue(nextValue);
    validateAssortment(nextValue);
  }

  async function handleStateChange(state: OptionV2 | null) {
    const nextValue = { ...value, address: { ...value.address, state, city: null } };
    updateValue(nextValue);
    if (state) {
      const data = await fetchStateCities(String(state.value));
      setCities(data.map((city: any) => ({ label: city.nome, value: city.id })));
    } else setCities([]);
  }

  async function handleNext() {
    if (value.whoiam?.value === "fazenda") {
      setPayload((current) => {
        const next = {
          ...current,
          assortment: {
            whoiam: value.whoiam,
            name: "",
            document: null,
            state_registration: "",
            phone: "",
            address: {
              city: null,
              state: null,
              street: "",
            },
          },
        };
        persistPayload(next);
        return next as any;
      });
      return true;
    }

    const isValid = await validateAssortment();
    if (!isValid) return false;

    setPayload((current) => {
      const next = {
        ...current,
        assortment: {
          ...value,
          document: value.document || null,
          address: {
            city: value.address.city
              ? {
                  code: value.address.city.value,
                  name: value.address.city.label,
                }
              : null,
            state: value.address.state?.value || null,
            street: value.address.street || "",
          },
        },
      };
      persistPayload(next);
      return next as any;
    });
    return true;
  }

  useEffect(() => {
    fetchStatesUF().then((ufs) => setStates(ufs.map((uf: string) => ({ label: uf, value: uf }))));
  }, []);

  useEffect(() => {
    if (payload?.assortment) {
      const assortment: any = payload.assortment;
      setValue({
        whoiam: assortment.whoiam || null,
        name: assortment.name || "",
        document: assortment.document || "",
        state_registration: assortment.state_registration || "",
        phone: assortment.phone || "",
        address: {
          state: assortment.address?.state ? { label: assortment.address.state, value: assortment.address.state } : null,
          city: assortment.address?.city?.code
            ? { label: assortment.address.city.name, value: assortment.address.city.code }
            : null,
          street: assortment.address?.street || "",
        },
      });
    }
  }, [payload]);

  useEffect(() => {
    const apiErrors = Object.entries(apiFieldErrors).reduce((nextErrors, [field, message]) => {
      if (!field.startsWith("assortment.")) return nextErrors;
      nextErrors[field.replace("assortment.", "")] = message;
      return nextErrors;
    }, {} as ValidationErrorsV2);

    if (hasErrors(apiErrors)) setErrors((current) => ({ ...current, ...apiErrors }));
  }, [apiFieldErrors]);

  useEffect(() => {
    setBlockNextStep(false);
  }, [setBlockNextStep]);

  return (
    <>
      <main>
        <div className="onboarding-v2-form-page">
          <FormCardV2 title="Agropecuária" description="Informe se o cadastro representa uma agropecuária ou uma fazenda.">
            <FormGridV2>
              <DropdownV2
                name="whoiam"
                label="Você está cadastrando"
                value={value.whoiam}
                options={whoIamOptions}
                error={errors.whoiam}
                onBlur={() => validateAssortment()}
                onChange={(whoiam) => updateValue({ ...value, whoiam })}
              />
            </FormGridV2>
            {value.whoiam?.value === "agropecuaria" && (
              <FormGridV2>
                <InputV2
                  name="name"
                  label="Nome Fantasia"
                  value={value.name}
                  error={errors.name}
                  onBlur={() => validateAssortment()}
                  onChange={(name) => updateValue({ ...value, name })}
                />
                <InputV2
                  name="document"
                  label="CPF / CNPJ"
                  value={value.document}
                  mask="cpfCnpj"
                  error={errors.document}
                  onBlur={() => validateAssortment()}
                  onChange={(document) => updateValue({ ...value, document })}
                />
                <InputV2
                  name="state_registration"
                  label="Inscrição Estadual"
                  value={value.state_registration}
                  error={errors.state_registration}
                  onBlur={() => validateAssortment()}
                  onChange={(state_registration) => updateValue({ ...value, state_registration })}
                />
                <InputV2
                  name="phone"
                  label="Telefone"
                  value={value.phone}
                  type="tel"
                  mask="phone"
                  error={errors.phone}
                  onBlur={() => validateAssortment()}
                  onChange={(phone) => updateValue({ ...value, phone })}
                />
                <DropdownV2
                  name="address.state"
                  label="UF"
                  value={value.address.state}
                  options={states}
                  searchable
                  error={errors["address.state"]}
                  onBlur={() => validateAssortment()}
                  onChange={handleStateChange}
                />
                <DropdownV2
                  name="address.city"
                  label="Cidade"
                  value={value.address.city}
                  options={cities}
                  searchable
                  disabled={!value.address.state}
                  error={errors["address.city"]}
                  onBlur={() => validateAssortment()}
                  onChange={(city) => updateValue({ ...value, address: { ...value.address, city } })}
                />
                <InputV2
                  name="address.street"
                  label="Endereço"
                  value={value.address.street}
                  onBlur={() => validateAssortment()}
                  onChange={(street) => updateValue({ ...value, address: { ...value.address, street } })}
                />
              </FormGridV2>
            )}
          </FormCardV2>
        </div>
      </main>
      <OnboardingFooterV2 nextStepEvent={handleNext} />
    </>
  );
}
